/* Hallmark · component: document-import-dialog · genre: editorial · theme: Operational Architect
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass via semantic Operational Architect tokens
 */
import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { AlertTriangle, CheckSquare, FileText, UploadCloud, X } from 'lucide-react';
import { Button } from '@lms/ui';
import type { Program } from '@lms/shared/types';
import { useProgram } from '../hooks/usePrograms';
import { useBulkCreateQuestionBankQuestions } from '../hooks/useQuestionBank';
import { getProgramSubjects } from './question-bank-utils';
import { parseQuestionsFromText, validateParsedQuestion, type ParsedQuestion } from '../utils/questionParser';

interface QuestionBankDocumentImportDialogProps {
  programs: Program[];
  onClose: () => void;
  onImported: () => void;
}

export function QuestionBankDocumentImportDialog({
  programs,
  onClose,
  onImported,
}: QuestionBankDocumentImportDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [programId, setProgramId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [rawText, setRawText] = useState('');
  const [questions, setQuestions] = useState<ParsedQuestion[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const { data: selectedProgram } = useProgram(programId);
  const subjects = useMemo(() => getProgramSubjects(selectedProgram), [selectedProgram]);
  const bulkCreate = useBulkCreateQuestionBankQuestions();

  const parse = (text: string) => {
    const parsed = parseQuestionsFromText(text).map((question) => ({
      ...question,
      warnings: validateParsedQuestion(question),
    }));
    setRawText(text);
    setQuestions(parsed);
    setSelectedIds(parsed.filter((question) => !question.warnings?.length).map((question) => question.id ?? ''));
    setError(parsed.length ? '' : 'Format belum menghasilkan soal. Periksa contoh format atau isi teks soal.');
  };

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => parse(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => setError('Berkas belum dapat dibaca. Gunakan TXT, CSV, JSON, atau Markdown.');
    reader.readAsText(file);
  };

  const toggle = (id: string) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const importQuestions = async () => {
    if (!programId) {
      setError('Pilih program tujuan sebelum mengimpor.');
      return;
    }
    const selected = questions.filter((question) => question.id && selectedIds.includes(question.id));
    if (!selected.length) {
      setError('Pilih minimal satu soal yang valid untuk diimpor.');
      return;
    }
    try {
      await bulkCreate.mutateAsync({
        programId,
        subjectId: subjectId || undefined,
        questions: selected.map((question) => ({
          question: question.question,
          type: question.type,
          explanation: question.explanation,
          points: question.points,
          options: question.options,
        })),
      });
      onImported();
      onClose();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Soal belum dapat diimpor.');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-on-surface/45 p-0 backdrop-blur-sm sm:items-center sm:p-6">
      <section className="max-h-[94vh] w-full max-w-4xl overflow-y-auto rounded-t-[1.75rem] bg-surface-container-lowest shadow-level5 sm:rounded-[1.75rem]" aria-labelledby="document-import-title">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 bg-surface-container-lowest/90 p-6 backdrop-blur-xl sm:p-8">
          <div>
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">Impor bank soal</p>
            <h2 id="document-import-title" className="mt-1 font-headline text-2xl font-bold tracking-tight text-on-surface">Pratinjau sebelum simpan</h2>
            <p className="mt-2 text-sm text-on-surface-variant">Baca TXT, CSV, JSON, atau Markdown; soal bermasalah dapat dibatalkan sebelum masuk bank.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Tutup impor dokumen" className="grid size-11 place-items-center rounded-xl text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"><X size={18} /></button>
        </header>

        <div className="space-y-7 p-6 pt-2 sm:p-8 sm:pt-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="import-program" className="mb-2 block text-xs font-label font-bold text-on-surface">Program tujuan <span className="text-error">*</span></label>
              <select id="import-program" value={programId} onChange={(event) => { setProgramId(event.target.value); setSubjectId(''); }} className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20">
                <option value="">Pilih program</option>
                {programs.map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="import-subject" className="mb-2 block text-xs font-label font-bold text-on-surface">Mata pelajaran <span className="font-normal text-on-surface-variant">(opsional)</span></label>
              <select id="import-subject" value={subjectId} onChange={(event) => setSubjectId(event.target.value)} disabled={!programId} className="w-full rounded-xl bg-surface-container-high px-4 py-3 text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50">
                <option value="">Umum untuk program</option>
                {subjects.map((subject) => <option key={subject.id} value={subject.id}>{subject.name}</option>)}
              </select>
            </div>
          </div>

          <div className="rounded-2xl bg-surface-container-low p-4 sm:p-5">
            <input ref={inputRef} type="file" accept=".txt,.csv,.tsv,.json,.md,text/plain,text/csv,application/json,text/markdown" onChange={onFile} className="sr-only" />
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3"><div className="grid size-11 place-items-center rounded-2xl bg-primary/10 text-primary"><UploadCloud size={20} /></div><div><h3 className="m-0 text-sm font-headline font-bold text-on-surface">Pilih berkas atau tempel teks</h3><p className="mt-1 text-xs leading-relaxed text-on-surface-variant">Format CSV/TSV, JSON, dan Markdown dikenali otomatis.</p></div></div>
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} className="h-10 shrink-0 rounded-xl px-4 text-xs font-bold active:scale-95"><FileText size={15} /> Pilih dokumen</Button>
            </div>
            <label htmlFor="import-content" className="sr-only">Konten dokumen soal</label>
            <textarea id="import-content" value={rawText} onChange={(event) => setRawText(event.target.value)} rows={8} className="mt-5 w-full resize-y rounded-xl bg-surface-container-lowest px-4 py-3 font-mono text-xs leading-relaxed text-on-surface outline-none focus:ring-2 focus:ring-primary/20" placeholder={'1. Pertanyaan pertama\nA. Opsi satu\nB. Opsi dua\nKunci: A'} />
            <Button type="button" variant="ghost" onClick={() => parse(rawText)} disabled={!rawText.trim()} className="mt-3 h-10 rounded-xl px-4 text-xs font-bold text-primary active:scale-95">Baca & pratinjau</Button>
          </div>

          {questions.length > 0 && (
            <section>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3"><div><h3 className="m-0 text-sm font-headline font-bold text-on-surface">Hasil pratinjau</h3><p className="mt-1 text-xs text-on-surface-variant">{selectedIds.length} dari {questions.length} soal valid dipilih.</p></div><button type="button" onClick={() => setSelectedIds(selectedIds.length === questions.filter((question) => !question.warnings?.length).length ? [] : questions.filter((question) => !question.warnings?.length).map((question) => question.id ?? ''))} className="inline-flex h-9 items-center gap-2 rounded-lg bg-surface-container-high px-3 text-xs font-bold text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary active:scale-95"><CheckSquare size={15} /> Pilih valid</button></div>
              <div className="space-y-3">
                {questions.map((question, index) => {
                  const id = question.id ?? String(index);
                  const hasWarnings = Boolean(question.warnings?.length);
                  return <label key={id} className={`block cursor-pointer rounded-2xl p-4 transition-colors ${hasWarnings ? 'bg-error-container/50 text-on-error-container' : selectedIds.includes(id) ? 'bg-surface-container-low text-on-surface' : 'bg-surface-container text-on-surface-variant'}`}><div className="flex items-start gap-3"><input type="checkbox" checked={selectedIds.includes(id)} disabled={hasWarnings} onChange={() => toggle(id)} className="mt-1 size-4 accent-primary" /><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2 text-[10px] font-label font-bold uppercase tracking-wider"><span>{index + 1}</span><span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{question.type.replace('_', ' ')}</span><span>{question.points} poin</span></div><p className="mt-2 text-sm font-semibold leading-relaxed">{question.question}</p>{question.options.length > 0 && <p className="mt-2 text-xs text-on-surface-variant">{question.options.map((option) => `${option.isCorrect ? '✓' : '○'} ${option.text}`).join(' · ')}</p>}{hasWarnings && <p className="mt-2 flex items-center gap-2 text-xs font-medium"><AlertTriangle size={15} /> {question.warnings?.join(' · ')}</p>}</div></div></label>;
                })}
              </div>
            </section>
          )}

          {error && <p role="alert" className="rounded-xl bg-error-container px-4 py-3 text-xs font-medium text-on-error-container">{error}</p>}
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end"><Button type="button" variant="ghost" onClick={onClose} disabled={bulkCreate.isPending} className="h-11 rounded-xl px-5 text-xs font-bold active:scale-95">Batal</Button><Button type="button" variant="primary" onClick={() => void importQuestions()} disabled={bulkCreate.isPending || selectedIds.length === 0} className="h-11 rounded-xl px-5 text-xs font-bold active:scale-95">{bulkCreate.isPending ? 'Mengimpor…' : `Simpan ${selectedIds.length} soal`}</Button></div>
        </div>
      </section>
    </div>
  );
}
